import { FiberDataInputValues } from '../../../../shared/installation-report-content-v2/splicing-and-measuring-content/fiber-data/fiber-data-entites';
import { Building } from '../../../entities/installation-reporting-mdu-details.entities';
import {
  BuildingSectionResponse,
  CommissionV2Response,
  CommissionV2Request,
  InstallationReportV2Request,
  MeasuringV2Response,
  MeasuringV2Request,
  PlotDesignV2Response,
  PreInstallationV2Request,
  PreInstallationV2Response,
  SectionV2Response,
  SectionV2Request,
  SelfInspectionV2Response,
} from '../model/installation-reporting-mdu-bff.entities';
import {
  InstallationReportSectionConfig,
  InstallationReportSectionId,
} from '../model/installation-reporting-mdu-section.types';
import { isSelfInspectionAnswerComplete } from './self-inspection-answer.util';
import {
  BuildingReportSectionStates,
  ItemAnswer,
  SectionState,
} from './installation-reporting-mdu-section-state.types';

export const emptyFiberData = (): FiberDataInputValues => ({
  switchName: '',
  switchCard: '',
  switchPort: '',
  odfInSite: '',
  odfContact: '',
  cabinet: '',
  cableIdCabinet: '',
  fiberInCabinet: '',
  ductColorInCabinet: '',
  splitterName: '',
  splitterPort: '',
});

export const emptyItemAnswer = (): ItemAnswer => ({
  completed: false,
  files: [],
  attachments: [],
  isNotCompletable: false,
  isNotCompletableComment: null,
  updatedBy: null,
  lastUpdatedAt: null,
});

export const emptySectionState = (
  config: InstallationReportSectionConfig,
): SectionState => ({
  comment: '',
  status: null,
  itemAnswers: Object.fromEntries(
    config.items.map((item) => [item.id, emptyItemAnswer()]),
  ),
  showControlled: false,
  notApplicable: false,
  notApplicableComment: null,
  notApplicableUpdatedBy: null,
  notApplicableUpdatedAt: null,
  usesPanelCard: true,
  fiberData: emptyFiberData(),
  imageChecklist: Object.fromEntries(
    config.imageChecklist.map((item) => [item.field, false]),
  ),
  attachments: [],
  uploadedFiles: [],
  signing: null,
  macAddress: null,
  macAddressNetadmin: null,
});

const toDate = (value?: Date | string | null): Date | undefined => {
  if (!value) {
    return undefined;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }
  const parts = value.split('-').map(Number);
  if (parts.length === 3 && !parts.some((part) => Number.isNaN(part))) {
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const dateToIsoString = (date?: Date): string | null =>
  date ? date.toISOString() : null;

const isMeasuringResponse = (
  section: BuildingSectionResponse | SectionV2Response,
): section is MeasuringV2Response => 'hasSplicingFiberOutletImage' in section;

const isCommissionResponse = (
  section: BuildingSectionResponse | SectionV2Response,
): section is CommissionV2Response =>
  'hasNodeImage' in section && !('hasSplicingFiberOutletImage' in section);

const isPlotDesignResponse = (
  section: BuildingSectionResponse | SectionV2Response,
): section is PlotDesignV2Response => 'signing' in section;

const isPreInstallationResponse = (
  section: BuildingSectionResponse | SectionV2Response,
): section is PreInstallationV2Response => 'macAddress' in section;

const fiberDataFromMeasuring = (
  section: MeasuringV2Response,
): FiberDataInputValues => ({
  switchName: section.switchName ?? '',
  switchCard: section.switchCard ?? '',
  switchPort: section.switchPort ?? '',
  odfInSite: section.odfInSite ?? '',
  odfContact: section.odfContact ?? '',
  cabinet: section.cabinet ?? '',
  cableIdCabinet: section.cableIdCabinet ?? '',
  fiberInCabinet: section.fiberInCabinet ?? '',
  ductColorInCabinet: section.ductColorInCabinet ?? '',
  splitterName: section.splitterName ?? '',
  splitterPort: section.splitterPort ?? '',
});

const normalizeAttachments = (
  attachments: SelfInspectionV2Response['attachments'] | undefined,
): ItemAnswer['attachments'] => {
  if (!attachments) {
    return [];
  }
  if (Array.isArray(attachments)) {
    return attachments;
  }
  return Object.values(
    attachments as Record<string, ItemAnswer['attachments'][number]>,
  );
};

const applySelfInspections = (
  config: InstallationReportSectionConfig,
  itemAnswers: Record<string, ItemAnswer>,
  selfInspections: SelfInspectionV2Response[],
): Record<string, ItemAnswer> => {
  if (!selfInspections.length) {
    return itemAnswers;
  }

  const next = { ...itemAnswers };
  for (const inspection of selfInspections) {
    const itemId = config.items.find(
      (item) =>
        item.id === inspection.installationReportDefinitionId ||
        item.id === inspection.id,
    )?.id;
    if (!itemId) {
      continue;
    }
    next[itemId] = {
      ...(next[itemId] ?? emptyItemAnswer()),
      completed: !!inspection.completed,
      attachments: normalizeAttachments(inspection.attachments),
      isNotCompletable: !!inspection.isNotCompletable,
      isNotCompletableComment: inspection.isNotCompletableComment,
      updatedBy: inspection.updatedBy,
      lastUpdatedAt: inspection.lastUpdatedAt,
    };
  }
  return next;
};

type SectionResponse = BuildingSectionResponse | SectionV2Response;

const imageChecklistFromResponse = (
  config: InstallationReportSectionConfig,
  section: SectionResponse,
  current: SectionState,
): SectionState['imageChecklist'] => {
  const imageChecklist = { ...current.imageChecklist };
  for (const item of config.imageChecklist) {
    const value = (section as unknown as Record<string, unknown>)[item.field];
    if (typeof value === 'boolean') {
      imageChecklist[item.field] = value;
    }
  }
  return imageChecklist;
};

const selfInspectionsFromResponse = (
  section: SectionResponse,
): SelfInspectionV2Response[] => {
  const raw =
    'selfInspections' in section ? section.selfInspections : undefined;
  return Array.isArray(raw) ? raw : [];
};

const areAllItemsAnswered = (
  config: InstallationReportSectionConfig,
  itemAnswers: Record<string, ItemAnswer>,
): boolean =>
  config.items.length > 0 &&
  config.items.every((item) =>
    isSelfInspectionAnswerComplete(itemAnswers[item.id]),
  );

const statusFromResponse = (
  section: SectionResponse,
  current: SectionState,
): SectionState['status'] =>
  section.status === 'NOT_APPLICABLE'
    ? current.status
    : (section.status ?? current.status);

const notApplicableFromResponse = (
  section: SectionResponse,
  current: SectionState,
): Pick<
  SectionState,
  | 'notApplicable'
  | 'notApplicableComment'
  | 'notApplicableUpdatedBy'
  | 'notApplicableUpdatedAt'
> => ({
  notApplicable: section.notApplicable ?? current.notApplicable,
  notApplicableComment:
    section.notApplicableComment ?? current.notApplicableComment,
  notApplicableUpdatedBy: section.updatedBy ?? current.notApplicableUpdatedBy,
  notApplicableUpdatedAt:
    section.lastUpdatedAt ?? current.notApplicableUpdatedAt,
});

const measuringFromResponse = (
  section: SectionResponse,
  current: SectionState,
): Pick<SectionState, 'usesPanelCard' | 'fiberData'> =>
  isMeasuringResponse(section)
    ? {
        usesPanelCard: section.usesPanelCard ?? current.usesPanelCard,
        fiberData: fiberDataFromMeasuring(section),
      }
    : { usesPanelCard: current.usesPanelCard, fiberData: current.fiberData };

const attachmentsFromResponse = (
  section: SectionResponse,
  current: SectionState,
): SectionState['attachments'] =>
  (isMeasuringResponse(section) || isCommissionResponse(section)) &&
  section.attachments
    ? section.attachments
    : current.attachments;

const signingFromResponse = (
  section: SectionResponse,
  current: SectionState,
): SectionState['signing'] =>
  isPlotDesignResponse(section)
    ? (section.signing ?? current.signing)
    : current.signing;

const preInstallationFromResponse = (
  section: SectionResponse,
  current: SectionState,
): Pick<SectionState, 'macAddress' | 'macAddressNetadmin'> =>
  isPreInstallationResponse(section)
    ? {
        macAddress: section.macAddress ?? current.macAddress,
        macAddressNetadmin:
          section.macAddressNetadmin ?? current.macAddressNetadmin,
      }
    : {
        macAddress: current.macAddress,
        macAddressNetadmin: current.macAddressNetadmin,
      };

export const mapSectionResponseToSectionState = (
  config: InstallationReportSectionConfig,
  section: SectionResponse,
  current: SectionState = emptySectionState(config),
): SectionState => {
  const itemAnswers = applySelfInspections(
    config,
    current.itemAnswers,
    selfInspectionsFromResponse(section),
  );

  return {
    ...current,
    plannedDate: section.plannedDate ? toDate(section.plannedDate) : undefined,
    completedDate: section.completedDate
      ? toDate(section.completedDate)
      : undefined,
    comment: section.comment ?? current.comment ?? '',
    status: statusFromResponse(section, current),
    ...notApplicableFromResponse(section, current),
    itemAnswers,
    showControlled: areAllItemsAnswered(config, itemAnswers)
      ? true
      : current.showControlled,
    ...measuringFromResponse(section, current),
    imageChecklist: imageChecklistFromResponse(config, section, current),
    attachments: attachmentsFromResponse(section, current),
    signing: signingFromResponse(section, current),
    ...preInstallationFromResponse(section, current),
  };
};

const completedDateBySection = (
  building: Building,
  sectionId: InstallationReportSectionId,
): string | null | undefined => {
  switch (sectionId) {
    case 'PLOT_DESIGN':
      return building.plotDesign;
    case 'PRE_INSTALLATION':
      return building.preinstallation;
    case 'PLOT_EXCAVATION':
      return building.plotExcavation;
    case 'DUCT_CONNECTING':
      return building.ductConnection;
    case 'CABLE_BLOWING':
      return building.cableBlowing;
    case 'SPLICING_MEASURING':
      return building.splicingMeasuring;
    case 'COMMISSION':
      return building.commissioning;
  }
};

export const createBuildingReportSectionState = (
  config: InstallationReportSectionConfig,
  building: Building,
): SectionState => {
  const completedDate = toDate(completedDateBySection(building, config.id));
  const status = building.sectionStatuses?.[config.id]?.status;
  const fallbackStatus = completedDate ? 'EXECUTED' : 'PLANNED';
  const current = emptySectionState(config);
  current.status = fallbackStatus;
  return mapSectionResponseToSectionState(
    config,
    {
      comment: null,
      plannedDate: null,
      completedDate: completedDate ?? null,
      status: status ?? fallbackStatus,
    },
    current,
  );
};

export const mapSectionStateToRequest = (
  state: SectionState,
): SectionV2Request => ({
  comment: state.comment || null,
  plannedDate: dateToIsoString(state.plannedDate),
  completedDate: dateToIsoString(state.completedDate),
  status: state.status,
});

export const mapPreInstallationStateToRequest = (
  state: SectionState,
): PreInstallationV2Request => ({
  ...mapSectionStateToRequest(state),
  macAddress: state.macAddress,
});

export const mapMeasuringStateToRequest = (
  state: SectionState,
): MeasuringV2Request => ({
  ...mapSectionStateToRequest(state),
  usesPanelCard: state.usesPanelCard,
  ...state.fiberData,
  hasSplicingFiberOutletImage:
    state.imageChecklist.hasSplicingFiberOutletImage ?? false,
  hasSplicingFosImage: state.imageChecklist.hasSplicingFosImage ?? false,
  hasSealingDuctInFosImage:
    state.imageChecklist.hasSealingDuctInFosImage ?? false,
  hasOdfPortImage: state.imageChecklist.hasOdfPortImage ?? false,
});

export const mapCommissionStateToRequest = (
  state: SectionState,
): CommissionV2Request => ({
  ...mapSectionStateToRequest(state),
  hasOdfPortImage: state.imageChecklist.hasOdfPortImage ?? false,
  hasSwitchPortImage: state.imageChecklist.hasSwitchPortImage ?? false,
  hasNodeImage: state.imageChecklist.hasNodeImage ?? false,
});

export const mapBuildingReportSectionStatesToUpdateRequest = (
  states: BuildingReportSectionStates,
): InstallationReportV2Request => ({
  deliveryOrderId: null,
  plotDesign: mapSectionStateToRequest(states.PLOT_DESIGN),
  plotExcavation: mapSectionStateToRequest(states.PLOT_EXCAVATION),
  preInstallation: mapPreInstallationStateToRequest(states.PRE_INSTALLATION),
  commission: mapCommissionStateToRequest(states.COMMISSION),
  measuring: mapMeasuringStateToRequest(states.SPLICING_MEASURING),
  ductConnecting: mapSectionStateToRequest(states.DUCT_CONNECTING),
  cableBlowing: mapSectionStateToRequest(states.CABLE_BLOWING),
});
